import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClientProvider } from '@tanstack/react-query';
import theme from './theme';
import queryClient from './services/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './components/AppSnackbar';
import { NavigationGuardProvider } from './contexts/NavigationGuardContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AssetSpreadsheetPage from './pages/AssetSpreadsheetPage';
import AssetCreatePage from './pages/AssetCreatePage';
import AssetEditPage from './pages/AssetEditPage';
import AssetDetailPage from './pages/AssetDetailPage';
import ImportPage from './pages/ImportPage';
import LicenseSpreadsheetPage from './pages/LicenseSpreadsheetPage';
import UserManagementPage from './pages/UserManagementPage';
import MasterSettingsPage from './pages/MasterSettingsPage';
import CustomerSpreadsheetPage from './pages/CustomerSpreadsheetPage';
import ContractSpreadsheetPage from './pages/ContractSpreadsheetPage';
import CustomerKartePage from './pages/CustomerKartePage';
import TicketManagementPage from './pages/TicketManagementPage';

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SnackbarProvider>
          <NavigationGuardProvider>
          <BrowserRouter>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes - all roles */}
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/assets" element={<AssetSpreadsheetPage />} />
                  <Route path="/assets/:id" element={<AssetDetailPage />} />
                  <Route path="/licenses" element={<LicenseSpreadsheetPage />} />
                  <Route path="/contracts" element={<ContractSpreadsheetPage />} />
                  <Route path="/customers/:customerId/karte" element={<CustomerKartePage />} />
                  <Route path="/customers/:customerId/tickets" element={<TicketManagementPage />} />

                  {/* Admin-only routes */}
                  <Route path="/assets/new" element={<AssetCreatePage />} />
                  <Route path="/assets/:id/edit" element={<AssetEditPage />} />
                  <Route path="/assets/import" element={<ImportPage />} />
                  <Route path="/admin/users" element={<UserManagementPage />} />
                  <Route path="/admin/customers" element={<CustomerSpreadsheetPage />} />
                  <Route path="/admin/master" element={<MasterSettingsPage />} />
                </Route>
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </NavigationGuardProvider>
          </SnackbarProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
